import { observer } from "mobx-react";
import React from "react";

import { YoutubeBlockModel } from "@courses-models/course";

import { getIcon } from "@apps/viewer/src/assets/icons";
import { config } from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/config";

import {
  Preview,
  PreviewProps,
  PreviewState,
  PreviewTitle,
} from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/Preview";

/**
 * Configuration of the url used to fetch thumbnails for videos based on their
 * id.
 */
const YOUTUBE_THUMBNAIL_URL = "https://img.youtube.com/vi/{ id }/sddefault.jpg";

type YoutubePreviewState = PreviewState & {
  /**
   * Will contain the duration of the video, once it has been fetched from
   * the remote backend.
   */
  videoDuration?: string;
};

/**
 * Renders a preview of Youtube blocks consisting of title, thumbnail and video
 * duration (once fetched).
 */
@observer
class YoutubePreviewUnwrapped extends Preview<
  PreviewProps<YoutubeBlockModel>,
  YoutubePreviewState
> {
  /**
   * Set up initial state.
   */
  public readonly state: YoutubePreviewState = {
    isActive: false,
  };

  /**
   * Renderer that'll inject the fallback icon, which is going to be used if
   * no thumbnail could be found for this block.
   */
  protected renderThumbnailFallbackIcon(): React.JSX.Element {
    return (
      <i>
        {getIcon("video", { strokeColor: config.thumbnailIconColor })}

        <style jsx>{`
          /** Position the icon */
          i {
            /** Position at the bottom-left corner */
            position: absolute;
            top: 46px;
            right: -70px;

            /** Set up sizing */
            width: 271px;
            height: 207px;
          }
        `}</style>
      </i>
    );
  }

  /**
   * There are no SubTitles available for YouTube previews, simply return
   * title.
   */
  protected getTitle(): PreviewTitle {
    return {
      title: this.props.model.title,
    };
  }

  /**
   * For YouTube we can safely use the id of the embedded video as the key,
   * which our preview depends upon
   */
  protected getDataKey(): string | undefined {
    return this.props.model.videoId;
  }

  /**
   * MOCK - We need to implement a fetchContent, but there's really no other data
   * needed in order to render the preview for this block...
   */
  protected async fetchContent(): Promise<void> {
    return;
  }

  /**
   * Helper that'll return a url to the thumbnail of a given Youtube video.
   */
  protected async getThumbnailUrl(
    videoId: string | undefined,
  ): Promise<string | undefined> {
    // Bail out if no video has been identified
    if (videoId === undefined) {
      return;
    }

    // ... Otherwise resolve with the url to the thumbnail for this video
    return YOUTUBE_THUMBNAIL_URL.replace("{ id }", videoId);
  }
}

export const YoutubePreview = YoutubePreviewUnwrapped;
